import React from "react";
import Box from "@mui/material/Box";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import { useRowSelector, useStoreDispatch, useStoreSelector } from "../../../store/Hooks";
import { navSliceActions } from "../../../store/slices/NavSlice";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import useHttp from "../../../hooks/useHttp";
import { useEffect, useState } from "react";
import Interaction from "../../../models/interactions/Interaction";
import ExistingEntityPreview from "./ExistingEntityPreview";
import { interactionTreeSliceActions } from "../../../store/slices/InteractionTreeSlice";
import Interval from "../../../models/communications/Interval";
import IntervalSet from "../../../models/communications/IntervalSet";
import InteractionNodeInterval from "../../../models/interactions/InteractionNodeInterval";
import { stateDeepCopy } from "../../../helpers/DataUpdateHelper";
import InteractionNode from "../../../models/interactions/InteractionNode";
import { CreateNewInteractionMatrixRowReturnProps } from "../../../models/interactions/Interaction";
import InteractionSettings from "../../../models/config/InteractionSettings";
import CommunicationSet from "../../../models/communications/CommunicationSet";

const testing = process.env.REACT_APP_TEST_MODE === "test";

const style = {
  position: "absolute",
  top: "35%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  border: "8px solid rgb(17, 80, 142)",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  borderRadius: "20px 20px",
};

interface InteractionSearchOptionsProps {
  id: string | number | null;
  label: string | null;
}

const ExistingEntitySearch = () => {
  const { sendRequest: fetchData } = useHttp();

  const [value, setValue] = useState<string | number | null>(null);
  const [inputValue, setInputValue] = useState("");

  const selectedEntityID: number = useStoreSelector(
    (state) => state.nav.selectedEntityID,
  );

  const selectedEntity: any = useStoreSelector(
    (state) => state.nav.selectedEntity,
  );

  const [interactionsDropdownList, setInteractionsDropdownList] = useState<
    Array<any>
  >([]);

  const rightDrawerOpen: boolean = useStoreSelector(
    (state) => state.nav.rightDrawerOpen,
  );

  // Interaction Tree slice redux values
  const intervals: Array<Interval> = useStoreSelector(
    (state) => state.interactionTree.Intervals,
  );


  const interactionNodes: Array<InteractionNode> = useStoreSelector(
    (state) => state.interactionTree.InteractionNodes,
  );
  const interactionNodeIntervals: Array<InteractionNodeInterval> =
    useStoreSelector((state) => state.interactionTree.InteractionNodeIntervals);

  const dispatch = useStoreDispatch();

  // Nav slice redux values
  const thisIntervalSetID = useStoreSelector(
    (state) => state.nav.intervalSetID,
  );
  const thisIntervalID = useStoreSelector(
    (state) => state.nav.intervalID,
  );
  const interval = useRowSelector(Interval, thisIntervalID || 0);

  const thisSet = useStoreSelector((state) => state.nav.thisSet);
  const thisParent = useStoreSelector((state) => state.nav.thisParent);
  const existingInteractionSearchType: string = useStoreSelector(
    (state) => state.nav.existingInteractionSearchType,
  );
  const ordinal = useStoreSelector((state) => state.nav.ordinal);

  // if needed for intervalSet entities
  const [intervalsForThisSet, setIntervalsForThisSet] = useState<
    Array<Interval>
  >([]);

  useEffect(() => {
    if (existingInteractionSearchType == "intervalset" && selectedEntityID) {
      const route = "/interval";
      const url: string =
        process.env.REACT_APP_API_URL +
        route +
        "?interval_set_id=eq." +
        selectedEntityID;
      const requestSettings = {
        url: url,
        method: "GET",
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Accept-Profile": testing ? "test" : "build",
        },
      };
      fetchData(requestSettings, (data: Array<Interval>) => {
        setIntervalsForThisSet(data);
      });
    }
  }, [selectedEntityID, existingInteractionSearchType, fetchData]);

  useEffect(() => {  
    let route = "/interaction?shared=eq.true";
    if (existingInteractionSearchType == "interval") {
      // if accessed from intervalSet, we only want to add
      // existing intervals that arent already on other sets
      if (thisIntervalSetID) {
        route = "/interval?interval_set_id=is.null";
      } else {
        route = "/interval";
      }
    } else if (existingInteractionSearchType == "intervalset") {
      route = "/interval_set?shared=eq.true";
    } else if (existingInteractionSearchType == "commset") {
      route = "/communication_set?shared=eq.true";
    }
    const url: string = process.env.REACT_APP_API_URL + route;
    const requestSettings = {
      url: url,
      method: "GET",
      headers: {
        Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
        "Content-Type": "application/json",
        "Accept-Profile": testing ? "test" : "build",
      },
    };
    fetchData(requestSettings, (data: Array<any>) => {
      if (existingInteractionSearchType == "interval") {
        handleIntervals(data);
      } else if (
        existingInteractionSearchType == "interaction" ||
        existingInteractionSearchType == "cs1survey" ||
        existingInteractionSearchType == "pathway"
      ) {
        handleInteractions(data);
      } else if (existingInteractionSearchType == "intervalset") {
        handleIntervalSets(data);
      } else if (existingInteractionSearchType == "commset") {
        handleCommSets(data);
      }
    });
  }, []);

  const handleInteractions = (data: Array<Interaction>) => {
    const filteredInteractions = data.filter((interaction) => {
      return interaction.properties.type === existingInteractionSearchType;
    });
    const interactionsDropdownList = filteredInteractions.map((option) => ({
      id: option.id,
      label: option.title,
    }));
    setInteractionsDropdownList(interactionsDropdownList);
  };

  const handleIntervalSets = (data: Array<IntervalSet>) => {
    // we only care about sets with names
    const intervalSetsDropdownList = data.reduce(
      (accumulator: any, option: IntervalSet) => {
        if (typeof option.name === "string") {
          accumulator.push({
            id: option.id,
            label: option.name,
          });
        }
        return accumulator;
      },
      [],
    );
    setInteractionsDropdownList(intervalSetsDropdownList);
  };

  const handleCommSets = (data: Array<CommunicationSet>) => {
    // we only care about sets with names
    const commSetsDropdownList = data.reduce(
      (accumulator: any, option: CommunicationSet) => {
        if (typeof option.name === "string") {
          accumulator.push({
            id: option.id,
            label: option.name,
          });
        }
        return accumulator;
      },
      [],
    );
    setInteractionsDropdownList(commSetsDropdownList);
  };

  const handleIntervals = (data: Array<Interval>) => {
    const intervalsDropdownList = data.map((option) => ({
      id: option.id,
      label: option.name,
    }));
    setInteractionsDropdownList(intervalsDropdownList);
  };

  useEffect(() => {
    // change route based on entity
    let route = "/interaction";
    if (existingInteractionSearchType == "interval") {
      route = "/interval";
    } else if (existingInteractionSearchType == "intervalset") {
      route = "/interval_set";
    } else if (existingInteractionSearchType == "commset") {
      route = "/communication_set";
    }
    if (selectedEntityID) {
      const url: string =
        process.env.REACT_APP_API_URL + route + "?id=eq." + selectedEntityID;
      const requestSettings = {
        url: url,
        method: "GET",
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Accept-Profile": testing ? "test" : "build",
        },
      };
      fetchData(
        requestSettings,
        (data: Array<Interaction | Interval | IntervalSet | CommunicationSet>) => {
          dispatch(navSliceActions.setSelectedEntity({selectedEntity:data[0]}))
        },
      );
    }
  }, [selectedEntityID, fetchData]);

  // flexible function for picking an existing item from the db and adding it to another
  // add an instance of an existing interaction to a node OR
  // add an instance of an existing interval to an interaction (via matrix or standalone) OR
  // add an instance of an existing intervalSet to the UI of a Pathway interaction
  const addExistingEntity = async () => {
    console.log("happening");
    // for handling intervalsets
    // get only the intervals from redux that are in this here set
    let associatedIntervals: Array<Interval> = [];
    const associatedIntervalIDs: Array<number> = [];
    if (thisIntervalSetID) {
      associatedIntervals = intervals.filter(function (item) {
        return item.interval_set_id === thisIntervalSetID;
      });
    }
    associatedIntervals.forEach((interval: Interval) => {
      associatedIntervalIDs.push(interval.id);
    });
    let myordinal = 0;
    if (ordinal !== null && ordinal > -1) {
      myordinal = ordinal;
    }

    let data: any = {};
    let url: string;
    let requestSettings: any = {};

    if (
      thisIntervalSetID &&
      existingInteractionSearchType != "intervalset" &&
      existingInteractionSearchType != "interval"
    ) {
      // Adding existing interactions to an interval set matrix
      const thisInteraction = selectedEntity;
      // db update endpoint takes a comma-delimited string of intervalIDs
      // we need to extract them from associatedIntervals to formulate acceptable request param
      // i.e. '1,2,3'
      let interval_ids = "";
      let counter = 0;
      associatedIntervalIDs.forEach((id: number) => {
        counter += 1;
        interval_ids += id;
        // only add commas if we arent at the end
        if (counter !== associatedIntervals.length) {
          interval_ids += ",";
        }
      });

      Interaction.addNewInteractionToIntervalSet(associatedIntervalIDs,thisInteraction.title,{} as InteractionSettings,myordinal + 1,null,"",thisParent,thisSet)

    } else if (existingInteractionSearchType == "interval") {
      // existing intervals can be added in two ways:
      // 1. via the scheduling matrix
      // 2. directly on to stand-alone 'event' interactions
      const doesIntervalAlreadyExistIndex = intervals.findIndex((interval) => {
        return interval.id == selectedEntityID;
      });
      // we dont want to process the interval in central state more than once
      let intervalAdded = false;

      let thisTempInterval = new Interval(
        selectedEntityID,
        null, // null intervalSetID to start, we will add it in if an interval is being added to an intervalset
        selectedEntity.name,
        selectedEntity.start_day,
        selectedEntity.end_day,
        selectedEntity.communication_set_id,
        "2023-04-25T14:50:10.856423",
      );

      if (thisIntervalSetID) {
        // recreate the interval but with this intervalSetID added in
        thisTempInterval = new Interval(
          selectedEntityID,
          thisIntervalSetID,
          selectedEntity.name,
          selectedEntity.start_day,
          selectedEntity.end_day,
          selectedEntity.communication_set_id,
          "2023-04-25T14:50:10.856423",
        );
        const associatedInteractionNodeIntervals: Array<InteractionNodeInterval> =
          [];
        const associatedInteractionNodes: Array<InteractionNode> = [];

        // populate associatedInteractionNodeIntervals
        // limit logic to only nodes that fall directly under the currently supplied parent interactionID
        const validInteractionNodes = interactionNodes.filter(
          (interactionNode) => {
            return interactionNode.parent_interaction_id === thisParent;
          },
        );

        // for each interaction_node_interval, we want to see if its in array
        interactionNodeIntervals.forEach(function (interactionNodeInterval) {
          let isValidNode = false;
          // run each interactionNodeInterval through the
          const validNodes = validInteractionNodes.filter(
            (tempInteractionNode) => {
              return (
                interactionNodeInterval.interaction_node_id ===
                tempInteractionNode.id
              );
            },
          );
          if (validNodes.length > 0) {
            isValidNode = true;
          }
          associatedIntervals.forEach(function (associatedInterval) {
            if (
              interactionNodeInterval.interval_id === associatedInterval.id &&
              isValidNode &&
              !associatedInteractionNodeIntervals.includes(
                interactionNodeInterval,
              )
            ) {
              associatedInteractionNodeIntervals.push(interactionNodeInterval);
            }
          });
        });

        // start pulling the rest of the matrix data from this refined list of nodes
        validInteractionNodes.forEach(function (interactionNode) {
          associatedInteractionNodeIntervals.forEach(
            function (associatedInteractionNodeInterval) {
              if (
                interactionNode.id ===
                  associatedInteractionNodeInterval.interaction_node_id &&
                !associatedInteractionNodes.includes(interactionNode)
              ) {
                associatedInteractionNodes.push(interactionNode);
              }
            },
          );
        });
        // for each interactionNode associated with this interval set,
        // add a corresponding interaction node interval with the added intervalID
        associatedInteractionNodes.forEach(
          (interactionNode: InteractionNode) => {
            const data2 = {
              interaction_node_id: interactionNode.id,
              interval_id: selectedEntityID,
              target: "subject",
              condition_id: null,
              communication_set_id: null,
            };
            // not sure if this function actually exists or what its structure is
            // it needs to return and id
            const url2 =
              process.env.REACT_APP_API_URL +
              "/rpc/create_new_interaction_node_interval";
            const requestSettings2 = {
              url: url2,
              method: "POST",
              body: data2,
              headers: {
                Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
                "Content-Type": "application/json",
                "Content-Profile": testing ? "test" : "build"
              },
            };
            if (process.env.REACT_APP_DEV_MODE === "OFF") {
              fetchData(requestSettings2, (data: any) => {
                const insertedInteractionNodeIntervalID = data;
                if (insertedInteractionNodeIntervalID && thisParent) {
                  let newInteractionNodeInterval = new InteractionNodeInterval(
                    insertedInteractionNodeIntervalID,
                    interactionNode.id,
                    selectedEntityID,
                    null,
                    null,
                    "subject",
                    "1900-01-01 00:00:00",
                  );
                  newInteractionNodeInterval = stateDeepCopy(
                    newInteractionNodeInterval,
                  );
                  dispatch(
                    interactionTreeSliceActions.addInteractionNodeInterval(
                      newInteractionNodeInterval,
                    ),
                  );
                  // interval exists, but not currently in UI
                  // if interval isnt already in central state, add it
                  if (doesIntervalAlreadyExistIndex == -1) {
                    if (!intervalAdded) {
                      dispatch(
                        interactionTreeSliceActions.setIntervals({
                          intervals: [thisTempInterval],
                          append: true,
                        }),
                      );
                      intervalAdded = true;
                    }
                  } else {
                    // if interval IS already in central state, we dont need to add it again
                    // just need to update the intervalsetID on the interval
                    if (!intervalAdded) {
                      dispatch(
                        interactionTreeSliceActions.updateInterval({
                          id: selectedEntityID,
                          interval_set_id: thisIntervalSetID,
                        }),
                      );
                      intervalAdded = true;
                    }
                  }
                }
              });
            } else {
              // TODO/FIXME - update UI when testing but dont update db
            }
          },
        );
        // update interval in backend with new intervalSetID
        const data = thisTempInterval;
        const url: string =
          process.env.REACT_APP_API_URL + "/interval?id=eq." + selectedEntityID;
        const requestSettings = {
          url: url,
          method: "PUT",
          body: data,
          headers: {
            Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
            "Content-Type": "application/json",
            "Content-Profile": testing ? "test" : "build"
          },
        };
        if (process.env.REACT_APP_DEV_MODE === "OFF") {
          fetchData(requestSettings);
        }
        // we want to add an interval, but to an INDIVIDUAL INTERACTION and
        // NOT to an intervalSet
      } else {
        data = {
          interaction_node_id: thisParent,
          interval_id: selectedEntityID,
          target: "subject",
          condition_id: null,
          communication_set_id: null,
        };
        // not sure if this function actually exists or what its structure is
        // it needs to return and id
        url =
          process.env.REACT_APP_API_URL +
          "/rpc/create_new_interaction_node_interval";
        requestSettings = {
          url: url,
          method: "POST",
          body: data,
          headers: {
            Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
            "Content-Type": "application/json",
            "Content-Profile": testing ? "test" : "build"
          },
        };
        if (process.env.REACT_APP_DEV_MODE === "OFF") {
          await fetchData(requestSettings, (data: any) => {
            // if interval is already in central state, we dont need to add it again
            // just need to update the intervalsetID on the interval
            const doesIntervalAlreadyExistIndex = intervals.findIndex(
              (interval) => {
                return interval.id == selectedEntityID;
              },
            );
            // interval does NOT already exist, so add it
            if (doesIntervalAlreadyExistIndex == -1) {
              dispatch(
                interactionTreeSliceActions.setIntervals({
                  intervals: [stateDeepCopy(thisTempInterval)],
                  append: true,
                }),
              );
            }
            // add the new interaction node interval for the single interaction
            const insertedInteractionNodeIntervalID = data;
            if (insertedInteractionNodeIntervalID && thisParent) {
              let newInteractionNodeInterval = new InteractionNodeInterval(
                insertedInteractionNodeIntervalID,
                thisParent,
                selectedEntityID,
                null,
                null,
                "subject",
                "1900-01-01 00:00:00",
              );
              newInteractionNodeInterval = stateDeepCopy(
                newInteractionNodeInterval,
              );
              dispatch(
                interactionTreeSliceActions.addInteractionNodeInterval(
                  newInteractionNodeInterval,
                ),
              );
            }
          });
        } else {
          // TODO - fix mock for tests
        }
      }
    } else if (existingInteractionSearchType == "intervalset") {
      IntervalSet.addExisting(selectedEntityID,thisSet || 0, thisParent || 0 ,myordinal)
    } else if (existingInteractionSearchType == "commset") {
      // add any fetched commsets to the current context
      CommunicationSet.fromJSON(selectedEntity);
      const updatedInterval = interval?.update("communication_set_id", selectedEntityID)

      // update interval in redux and db, but ONLY if we aren't in intervalEditor
      // if we are in intervalEditor, we just want to return the new comm set to that
      // (or at least make it available in redux, which it is via selectedEntity)
      if(!rightDrawerOpen){
        updatedInterval?.save()
      }

    } else {
      // Adding existing interaction directly to a pathway
      data = {
        theset: thisSet,
        theparent: thisParent,
        theordinal: ordinal ? ordinal + 1 : 1,
        thechild: selectedEntityID,
        thecondition_id: null,
      };
      url = process.env.REACT_APP_API_URL + "/rpc/create_new_interaction_node";
      requestSettings = {
        url: url,
        method: "POST",
        body: data,
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Content-Profile": testing ? "test" : "build"
        },
      };
      if (process.env.REACT_APP_DEV_MODE === "OFF") {
        await fetchData(requestSettings, (data: any) => {
          const insertedInteractionNodeID = data;
          if (insertedInteractionNodeID) {
            dispatch(
              interactionTreeSliceActions.addNewInteraction(selectedEntity),
            );
            dispatch(
              interactionTreeSliceActions.addNewInteractionNode({
                id: insertedInteractionNodeID,
                parentID: thisParent,
                setID: thisSet,
                childID: selectedEntityID,
                ordinal: ordinal ? ordinal : 1,
                condition: null,
                communication: null,
              }),
            );
          }
        });
      } else {
        const insertedInteractionNodeID = 77777;
        if (insertedInteractionNodeID) {
          dispatch(
            interactionTreeSliceActions.addNewInteraction(selectedEntity),
          );
          dispatch(
            interactionTreeSliceActions.addNewInteractionNode({
              id: insertedInteractionNodeID,
              parentID: thisParent,
              setID: thisSet,
              childID: selectedEntityID,
              ordinal: ordinal ? ordinal : 1,
              condition: null,
              communication: null,
            }),
          );
        }
      }
    }

    dispatch(navSliceActions.setSelectedEntityID({selectedEntityID:null}))
    dispatch(
      navSliceActions.closeModal(),
    );
  };

  return (
    <div>
      <Box sx={{ ...style, width: 800 }}>
        <h1
          style={{ color: "#1976d2", paddingBottom: "10px" }}
          id="parent-modal-title"
        >
          Add existing {existingInteractionSearchType}
        </h1>
        <p id="parent-modal-description">
          Start typing a title to initiate a search...
        </p>
        <Autocomplete
          value={value}
          onChange={(event: any, newValue: InteractionSearchOptionsProps) => {
            dispatch(navSliceActions.setSelectedEntityID({selectedEntityID:newValue.id}))
            setValue(newValue.label);
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          id="controllable-states-demo"
          options={interactionsDropdownList}
          sx={{ width: 800 }}
          renderInput={(params) => (
            <TextField {...params} label="Controllable" />
          )}
        />

        <Box sx={{ flexGrow: 1, marginTop: "20px" }}>
          <div style={{ textAlign: "center", paddingBottom: "10px" }}>
            <Button
              onClick={() => {
                dispatch(
                  navSliceActions.closeModal(),
                );
              }}
              color="warning"
              variant="contained"
            >
              <ArrowBackIcon />
              Exit Without Saving
            </Button>
            &nbsp;&nbsp;
            <Button
              variant="contained"
              color="success"
              onClick={addExistingEntity}
            >
              <AddIcon />
              ADD
            </Button>
          </div>

          {/* handle interaction preview */}
          {selectedEntityID &&
            selectedEntity &&
            (existingInteractionSearchType == "interaction" ||
              existingInteractionSearchType == "cs1survey" ||
              existingInteractionSearchType == "pathway") && (
              <ExistingEntityPreview
                type={existingInteractionSearchType}
                id={selectedEntityID}
                title={inputValue}
                interaction={selectedEntity}
              />
            )}
          {/* handle intervalset preview */}
          {selectedEntityID &&
            selectedEntity &&
            existingInteractionSearchType == "intervalset" && (
              <ExistingEntityPreview
                type={existingInteractionSearchType}
                id={selectedEntityID}
                title={inputValue}
                intervalSet={selectedEntity}
                intervalsForThisSet={intervalsForThisSet}
              />
            )}
          {/* handle interval preview */}
          {selectedEntityID &&
            selectedEntity &&
            existingInteractionSearchType == "interval" && (
              <ExistingEntityPreview
                type={existingInteractionSearchType}
                id={selectedEntityID}
                title={inputValue}
                interval={selectedEntity}
              />
            )}
                      {/* handle interval preview */}
          {selectedEntityID &&
            selectedEntity &&
            existingInteractionSearchType == "commset" && (
              <ExistingEntityPreview
                type={existingInteractionSearchType}
                id={selectedEntityID}
                title={inputValue}
                communicationSet={selectedEntity}
                preview={true}
              />
            )}
        </Box>
      </Box>
    </div>
  );
};

export default ExistingEntitySearch;
